<template>
  <div class="mb-5">
    <div v-if="!loadingDatasetDetails">
      <span
        property="dc:issued"
        :content="getReleaseDate"
      />
      <span
        property="dc:modified"
        :content="getModificationDate"
      />
      <!-- TEXT -->
      <div class="mt-4">
        <div class="row">
          <div
            class="col-10 offset-1"
            property="dc:description"
          >
            <p>{{ getTranslationFor(getDescription, $i18n.locale, getLanguages) | stripHtml }}</p>
          </div>
        </div>
      </div>
      <!-- INFO BANNERS -->
      <div class="mt-3">
        <div class="row">
          <div class="col-12">
            <!-- INCORRECT DATE BANNER -->
            <div
              v-if="!disableIncorrectDateAlert && dateIncorrect"
              class="p-0 py-3 text-center text-sm-left alert alert-secondary alert-dismissible fade show"
              role="alert"
            >
              <div class="row">
                <div class="col-1 m-auto text-center">
                  <b-icon-info-circle-fill
                    scale="1.5"
                    class="material-icons"
                  >
                    info
                  </b-icon-info-circle-fill>
                </div>
                <div class="col-10">
                  <p class="mb-0">
                    This dataset's last updated date is incorrect or incomplete, please contact the data provider for further inquiries.
                  </p>
                </div>
                <div class="col-1">
                  <button
                    class="close pt-0"
                    type="button"
                    data-dismiss="alert"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
            </div>
            <!-- MACHINE TRANSLATED BANNER -->
            <div
              v-if="!disableTranslationAlert && machineTranslated"
              class="p-0 py-3 text-center text-sm-left alert alert-secondary alert-dismissible fade show"
              role="alert"
            >
              <div class="row">
                <div class="col-1 m-auto text-center">
                  <b-icon-info-circle-fill
                    scale="1.5"
                    class="material-icons"
                  >
                    info
                  </b-icon-info-circle-fill>
                </div>
                <div class="col-10">
                  <p class="mb-0">
                    {{ $t('message.datasetDetails.translation.message') }}
                  </p>
                  <div v-if="showOriginalLanguage(getOriginalLanguage)">
                    <p class="mb-0">
                      {{ $t('message.datasetDetails.translation.original') }}: <strong>{{ getOriginalLanguage }}</strong>
                    </p>
                    <app-link :to="getDatasetOriginalLanguage(getOriginalLanguage)">
                      <button
                        class="alert-link font-weight-light btn btn-link btn-sm p-0"
                        @click="setDatasetOriginalLanguage(getOriginalLanguage)"
                      >
                        {{ $t('message.datasetDetails.translation.link') }}
                      </button>
                    </app-link>
                  </div>
                </div>
                <div class="col-1">
                  <button
                    class="close pt-0"
                    type="button"
                    data-dismiss="alert"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
            </div>
            <!-- TRANSLATION NOT AVAILABLE BANNER -->
            <div
              v-if="!disableTranslationAlert && translationNotAvailable"
              class="p-0 py-3 text-center text-sm-left alert alert-secondary alert-dismissible fade show"
              role="alert"
            >
              <div class="row">
                <div class="col-1 m-auto text-center">
                  <b-icon-info-circle-fill
                    scale="1.5"
                    class="material-icons"
                  >
                    info
                  </b-icon-info-circle-fill>
                </div>
                <div class="col-10">
                  <p class="mb-0">
                    {{ $t('message.datasetDetails.translation.noTranslation') }}
                  </p>
                </div>
                <div class="col-1">
                  <button
                    class="close pt-0"
                    type="button"
                    data-dismiss="alert"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
            </div>
            <!-- TRANSLATION UPDATE PROCESSING BANNER -->
            <div
              v-if="!disableTranslationAlert && translationUpdateProcessing"
              class="p-0 py-3 text-center text-sm-left alert alert-secondary alert-dismissible fade show"
              role="alert"
            >
              <div class="row">
                <div class="col-1 m-auto text-center">
                  <b-icon-info-circle-fill
                    scale="1.5"
                    class="material-icons"
                  >
                    info
                  </b-icon-info-circle-fill>
                </div>
                <div class="col-10">
                  <p class="mb-0">
                    {{ $t('message.datasetDetails.translation.update') }}
                  </p>
                </div>
                <div class="col-1">
                  <button
                    class="close pt-0"
                    type="button"
                    data-dismiss="alert"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- DISTRIBUTIONS -->
      <div class="mt-1">
        <div class="row">
          <div class="col-10 offset-1">
            <h3>{{ $t('message.metadata.distributions') }} ({{ getDistributions ? getDistributions.length : 0 }})</h3>
          </div>
          <ul class="list list-unstyled col-12">
            <hr>
            <div class="distributions">
              <div
                v-for="(distribution, i) in displayedDistributions"
                :key="`${i}--${distribution.id}`"
                class="distributions__item"
              >
                <!-- Preview and action overlay -->
                <div
                  v-if="!distributions.displayAll && !isDistributionsAllDisplayed && i === distributions.displayCount - 1"
                  class="distributions__item--preview"
                >
                  <!-- Fade out the last item so it has a preview feel -->
                  <!-- Render actions on top of it -->
                  <div class="distributions__actions pb-md-3">
                    <b-button
                      v-for="increment in distributions.incrementSteps.filter(nonOverflowingIncrements)"
                      :key="increment"
                      size="sm"
                      class="mr-1"
                      @click="increaseNumDistributionsDisplayed(increment)"
                    >
                      <b-icon icon="chevron-down" /> Show {{ increment }} more
                    </b-button>
                    <b-button
                      size="sm"
                      variant="primary"
                      @click="distributions.displayCount = getDistributions.length"
                    >
                      <b-icon icon="eye-fill" /> Show all ({{ remainingDistributions }})
                    </b-button>
                  </div>
                </div>
                <!-- Distribution rendering -->
                <li class="row">
                  <span class="d-inline-block col-md-1 col-2 pr-md-1 pr-0 m-md-0 m-auto">
                    <div
                      class="circle float-md-right text-center text-white"
                      :type="getDistributionFormat(distribution)"
                      :data-toggle="distributionFormatTruncated(distribution) ? 'tooltip' : false"
                      :data-placement="distributionFormatTruncated(distribution) ? 'top' : false"
                      :title="distributionFormatTruncated(distribution) ? getDistributionFormat(distribution) : false"
                    >
                      <span>{{ truncate(getDistributionFormat(distribution), 4, true) }}</span>
                    </div>
                  </span>
                  <span class="col-10">
                    <span class="row">
                      <span class="d-inline-block col-md-7 col-12">
                        <h6 class="m-0">{{ getDistributionTitle(distribution) }}</h6>
                        <span class="mt-2 d-block text-muted text-truncate">
                          <small>
                            <!-- {{ getDistributionDescription(distribution) }} -->
                          </small>
                        </span>
                        <small
                          v-if="showLicence(distribution)"
                          class="mt-2 d-block"
                        >
                          <span class="font-weight-bold">
                            <!-- TODO: Replace Licence text by icon -->
                            {{ $t('message.metadata.license') }}:
                          </span>
                          <app-link
                            :to="distribution.licence.resource"
                            target="_blank"
                            @click="$emit('trackLink', distribution.licence.resource, 'link')"
                          >
                          <span v-if='distribution.licence.title ==  "Data licence Germany – attribution – Version 2.0"'>Datenlizenz Deutschland – Namensnennung – Version 2.0</span>
                          <!--   {{ distribution.licence.title }} -->
                          </app-link>
                          <app-link
                            :to="distribution.licence.resource"
                            target="_blank"
                            @click="$emit('trackLink', distribution.licence.resource, 'link')"
                          >
                            <b-icon-info-circle-fill
                            v-if='distribution.licence.description == "Data licence Germany – attribution – Version 2.0"'
                              v-b-tooltip.hover.focus
                              :title='"Datenlizenz Deutschland – Namensnennung – Version 2.0"'
                              class="material-icons small-icon align-bottom spacing"
                            >info</b-icon-info-circle-fill>

                            <b-icon-info-circle-fill
                            v-else
                              v-b-tooltip.hover.focus
                              :title="distribution.licence.description"
                              class="material-icons small-icon align-bottom spacing"
                            >info</b-icon-info-circle-fill>
                          </app-link>
                          <!-- <app-link
                            v-if="showLicensingAssistant(distribution)"
                            :to="distribution.licence.la_url"
                            target="_blank"
                            @click="$emit('trackLink', distribution.licence.la_url, 'link')"
                          >
                            {{ $t('message.distributionLicense.licensingAssistant') }}
                          </app-link>
                          <app-link
                            v-if="showLicensingAssistant(distribution)"
                            :to="distribution.licence.la_url"
                            target="_blank"
                            @click="$emit('trackLink', distribution.licence.la_url, 'link')"
                          >
                            <b-icon-box-arrow-up-right>open_in_new</b-icon-box-arrow-up-right>
                          </app-link> -->
                        </small>
                        <small
                          v-else
                          class="text-muted"
                        >
                          {{ $t('message.distributionLicense.notProvided') }}
                        </small>
                      </span>
                      <span class="col-md-5 col-12 mt-2 text-md-right text-left">
                        <span class="d-inline-block">
                          <small class="pr-1">{{ filterDateFormatEU(distribution.releaseDate) }}</small>
                        </span>
                        <b-dropdown
                          v-if="!disableOptionsDropdown && showOptionsDropdown(distribution)"
                          class="options d-inline-block pr-1"
                          size="sm"
                          variant="secondary"
                          :text="$t('message.datasetDetails.options')"
                        >
                          <app-link
                            v-if="showVisualisationLink(distribution)"
                            class="dropdown-item px-3 text-right"
                            :path="getVisualisationLink(distribution.accessUrl)"
                            target="_blank"
                            @click="$emit('trackLink', getVisualisationLink(distribution.accessUrl), 'link')"
                          >
                            <b-dropdown-item-button>
                              <small class="px-2">{{ $t('message.datasetDetails.visualisation') }}</small>
                              <b-icon-bar-chart-fill>bar_chart</b-icon-bar-chart-fill>
                            </b-dropdown-item-button>
                          </app-link>
                          <app-link
                            v-if="showGeoLink(distribution)"
                            class="dropdown-item px-3 text-right"
                            :path="getGeoLink(distribution.format.title, distribution.id)"
                            target="_blank"
                            @click="$emit('trackLink', getGeoLink(distribution.format.title, distribution.id), 'link')"
                          >
                            <b-dropdown-item-button>
                              <small class="px-2">{{ $t('message.datasetDetails.geoVisualisation') }}</small>
                              <i class="material-icons float-right align-bottom">public</i>
                            </b-dropdown-item-button>
                          </app-link>
                        </b-dropdown>
                        <!--
                          NOTE: always use app-link>button instead of button>app-link due to IE11 issues with onClick events
                          Fixes https://gitlab.fokus.fraunhofer.de/piveau/organisation/piveau-scrum-board/-/issues/1369
                         -->
                        <app-link
                          v-if="showAccessUrl(distribution)"
                          class="text-decoration-none d-inline-block"
                          :to="distribution.accessUrl"
                          target="_blank"
                          rel="dcat:distribution noopener"
                          matomo-track-download
                          @after-click="trackGoto"
                        >
                          <b-button
                            tag="span"
                            class="download d-inline-block"
                            right
                            size="sm"
                            variant="primary"
                          >
                            Download
                          </b-button>
                        </app-link>
                        <b-button
                          v-if="showAccessUrl(distribution)"
                          class="download d-none"
                          right
                          text="Download"
                          size="sm"
                          variant="primary"
                        >
                          <b-dropdown-divider v-if="showDownloadUrls(distribution)" />
                          <div v-if="showDownloadUrls(distribution)">
                            <b-dropdown-form
                              v-for="downloadURL in distribution.downloadUrls"
                              :key="downloadURL"
                              class="resource-dropdown-form"
                            >
                              <b-form-group>
                                <div class="d-flex justify-content-between">
                                  <b-form-input
                                    disabled
                                    class="d-inline-block mr-2 py-0 w-100 small border border-secondary"
                                    size="sm"
                                    type="text"
                                    width="200px"
                                    :value="`${downloadURL}`"
                                  />
                                  <app-link
                                    class="d-inline-block mr-2  text-decoration-none"
                                    :to="downloadURL"
                                    target="_blank"
                                    matomo-track-download
                                    @after-click="trackGoto"
                                  >
                                    <b-icon-box-arrow-up-right
                                      scale="1.5"
                                    >open_in_new</b-icon-box-arrow-up-right>
                                  </app-link>
                                  <span
                                    class="d-inline-block copy-text "
                                    @click="setClipboard(downloadURL)"
                                  >
                                    <b-icon-file-text
                                      scale="1.5"
                                      class="material-icons"
                                    >file_copy
                                    </b-icon-file-text>
                                  </span>
                                </div>
                              </b-form-group>
                            </b-dropdown-form>
                          </div>
                        </b-button>
                      </span>
                    </span>
                  </span>
                </li>
                <hr>
              </div>
            </div>
          </ul>
        </div>
      </div>
      <!-- KEYWORDS -->
      <div class="row">
      <div class="col-10 pb-2 offset-1">
        <h3>{{ $t('message.datasetFacets.facets.keywords') }} ({{ getKeywords ? getKeywords.length : 0 }})</h3>
      </div>
      </div>
      <div
        v-if="showKeywords(getKeywords)"
        class="mt-2"
      >
        <div class="row">
          <div class="col-10 offset-1">
            <div class="row">
              <span
                v-for="(keyword, i) in getKeywords"
                v-if="showKeyword(keyword)"
                :key="i"
                class="col-4 col-sm-3 col-md-2 mt-md-0 mt-3 mb-2 pr-0"
                property="dcat:keyword"
                :content="keyword.title"
              >
                <app-link :to="getKeywordLink(keyword)">
                  <small
                    class="d-inline-block w-100 p-1 ml-1 rounded-pill text-center text-white bg-secondary"
                    :data-toggle="keywordTruncated(keyword) ? 'tooltip' : false"
                    :data-placement="keywordTruncated(keyword) ? 'top' : false"
                    :title="keywordTruncated(keyword) ? keyword.title : false"
                  >
                    {{ truncate(keyword.title, maxKeywordLength, false) }}
                  </small>
                </app-link>
              </span>
            </div>
          </div>
        </div>
      </div>
      <!-- MAP -->
      <div
        v-if="showSpatial(getSpatial)"
        class="mt-5"
      >
        <div class="row">
          <div
            class="col-1 my-auto pr-0 text-right"
            @click="toggleMap()"
          >
            <span
              v-if="mapVisible"
              class="arrow text-primary"
            >
              <b-icon-chevron-up
                scale="1.5"
                class="material-icons"
              >keyboard_arrow_up</b-icon-chevron-up>
            </span>
            <span
              v-else
              class="arrow text-primary"
            >
              <b-icon-chevron-down
                scale="1.5"
                class="material-icons"
              >keyboard_arrow_down</b-icon-chevron-down>
            </span>
          </div>
          <div class="col-10">
            <h3
              class="heading"
              @click="toggleMap()"
            >
              {{ $t('message.datasetDetails.geoInfo') }}
            </h3>
          </div>
          <div class="col-10 offset-1">
            <div
              id="collapse-geo-info"
              ref="geocollapse"
              class="collapse show"
            >
              <div class="map">
                <map-basic
                  width="100%"
                  height="400px"
                  :spatial-type="getSpatial.type"
                  :spatial-coordinates="getSpatial.coordinates"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- INFO -->
      <div class="mt-5">
        <div class="row">
          <div class="col-12">
            <div class="row">
              <div
                class="col-1 my-auto pr-0 text-right"
                @click="toggleInfo()"
              >
                <span
                  v-if="!infoVisible"
                  class="arrow text-primary"
                >
                  <b-icon-chevron-down
                    scale="1.5"
                    class="material-icons"
                  >keyboard_arrow_down</b-icon-chevron-down>
                </span>
                <span
                  v-else
                  class="arrow text-primary"
                >
                  <b-icon-chevron-up
                    scale="1.5"
                    class="material-icons"
                  >keyboard_arrow_up</b-icon-chevron-up>
                </span>
              </div>
              <div class="col-10 py-2">
                <h3
                  class="heading"
                  @click="toggleInfo()"
                >
                  {{ $t('message.datasetDetails.additionalInfo') }}
                </h3>
              </div>
              <div
                v-if="infoVisible"
                class="col-10 offset-1"
              >
                <table class="table table-borderless table-responsive pl-3 bg-light">
                  <tr v-if="isArray(getLandingPages) && getLandingPages.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.landingPage') }}
                    </td>
                    <td>
                      <div
                        v-for="(landingPage, i) of getLandingPages"
                        :key="i"
                      >
                        <app-link :to="landingPage || ''">
                          {{ landingPage }}
                        </app-link>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isArray(getSources) && getSources.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.sources') }}
                    </td>
                    <td>
                      <div
                        v-for="(source, i) of getSources"
                        :key="i"
                      >
                        <app-link
                          v-if="!isNil(source)"
                          :to="source"
                        >
                          {{ source }}
                        </app-link>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showSpatial(getSpatial)">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.spatial') }}
                    </td>
                    <td>
                      <div>{{ $t('message.metadata.coordinates') }}: {{ getSpatial ? getSpatial.coordinates : '' }}</div>
                      <div v-if="has(getSpatial, 'type') && !isNil(getSpatial.type)">
                        {{ $t('message.metadata.type') }}: {{ getSpatial ? getSpatial.type : '' }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="isObject(getPublisher) &&
                      ( (has(getPublisher, 'name') && !isNil(getPublisher.name)) ||
                      (has(getPublisher, 'email') && !isNil(getPublisher.email)) ||
                      (has(getPublisher, 'resource')) && !isNil(getPublisher.resource) )"
                  >
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.publisher') }}
                    </td>
                    <td>
                      <div v-if="has(getPublisher, 'name') && !isNil(getPublisher.name)">
                        {{ $t('message.metadata.name') }}: {{ getPublisher.name }}
                      </div>
                      <div v-if="has(getPublisher, 'email') && !isNil(getPublisher.email)">
                        {{ $t('message.metadata.email') }}: {{ getPublisher.email }}
                      </div>
                      <div v-if="has(getPublisher, 'resource') && !isNil(getPublisher.resource)">
                        {{ $t('message.metadata.resource') }}:
                        <app-link :to="getPublisher.resource">
                          {{ getPublisher.resource }}
                        </app-link>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isArray(getContactPoints) && getContactPoints.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.contactPoints') }}
                    </td>
                    <td>
                      <div
                        v-for="(contactPoint, i) in getContactPoints"
                        :key="i"
                      >
                        <div v-if="has(contactPoint, 'name') && !isNil(contactPoint.name)">
                          {{ $t('message.metadata.name') }}: {{ contactPoint.name }}
                        </div>
                        <div v-if="has(contactPoint, 'email') && !isNil(contactPoint.email)">
                          {{ $t($t('message.metadata.email')) }}:
                          <app-link :to="`mailto:${contactPoint.email}`">
                            {{ contactPoint.email }}
                          </app-link>
                        </div>
                        <div v-if="has(contactPoint, 'resource') && !isNil(contactPoint.resource)">
                          {{ $t('message.metadata.resource') }}:
                          <app-link :to="contactPoint.resource">
                            {{ contactPoint.resource }}
                          </app-link>
                        </div>
                        <br>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isArray(getConformsTo) && getConformsTo.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.conformsTo') }}
                    </td>
                    <td>
                      <div
                        v-for="(conformTo, i) in getConformsTo"
                        :key="i"
                      >
                        <div v-if="has(conformTo, 'title') && !isNil(conformTo.title)">
                          {{ $t('message.metadata.title') }}: {{ conformTo.title }}
                        </div>
                        <div v-if="has(conformTo, 'resource') && !isNil(conformTo.resource)">
                          {{ $t('message.metadata.resource') }}:
                          <app-link :to="conformTo.resource">
                            {{ conformTo.resource }}
                          </app-link>
                        </div>
                        <br>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isArray(getProvenances) && getProvenances.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.provenances') }}
                    </td>
                    <td>
                      <div
                        v-for="(provenance, i) in getProvenances"
                        :key="i"
                      >
                        <div v-if="has(provenance, 'label') && !isNil(provenance.label)">
                          {{ $t('message.metadata.label') }}: {{ provenance.label }}
                        </div>
                        <div v-if="has(provenance, 'resource') && !isNil(provenance.resource)">
                          {{ $t('message.metadata.resource') }}:
                          <app-link :to="provenance.resource">
                            {{ provenance.resource }}
                          </app-link>
                        </div>
                        <br>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isArray(getRelatedResources) && getRelatedResources.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.relatedResources') }}
                    </td>
                    <td>
                      <div
                        v-for="(resource, i) of getRelatedResources"
                        v-if="!isNil(resource)"
                        :key="i"
                      >
                        <app-link :to="resource">
                          {{ resource }}
                        </app-link>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isArray(getIdentifiers) && getIdentifiers.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.identifiers') }}
                    </td>
                    <td>
                      <div
                        v-for="(identifier, i) of getIdentifiers"
                        v-if="!isNil(identifier)"
                        :key="i"
                      >
                        {{ identifier }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isArray(getOtherIdentifiers) && getOtherIdentifiers.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.otherIdentifiers') }}
                    </td>
                    <td>
                      <div
                        v-for="(identifier, i) of getOtherIdentifiers"
                        v-if="!isNil(identifier)"
                        :key="i"
                      >
                        {{ identifier }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isArray(getDocumentations) && getDocumentations.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.documentations') }}
                    </td>
                    <td>
                      <div
                        v-for="(documentation, i) of getDocumentations"
                        v-if="!isNil(documentation)"
                        :key="i"
                      >
                        <app-link :to="documentation">
                          {{ documentation }}
                        </app-link>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="has(getFrequency, 'title') && !isNil(getFrequency.title)">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.metadata.frequency') }}
                    </td>
                    <td>
                      <div>{{ getFrequency.title }}</div>
                    </td>
                  </tr>
                  <tr v-if="has(getLegalBase, 'title') && !isNil(getLegalBase.title)">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.datasetFacets.facets.legalbase')}}
                    </td>
                    <td>
                      <div>
                        {{ getLegalBase.title }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="has(getGeoCodingDescription, 'title') && !isNil(getGeoCodingDescription.title)">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.datasetFacets.facets.geocodingdescription')}}
                    </td>
                    <td>
                      <div>
                        {{ getGeoCodingDescription.title }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isArray(getTemporal) && getTemporal.length > 0">
                    <td class="w-25 font-weight-bold">
                      {{ $t('message.datasetFacets.facets.temporal')}}
                    </td>
                    <td>
                      <div v-for="date in getTemporal">
                        {{ [filterDateFormatEU(date.lte),"DD.MM.YYYY HH:mm"] | moment(" MMMM YYYY") }}
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import Actions and Getters from Store Module
import { mapActions, mapGetters } from 'vuex';

// import helper functions
import {
  has,
  isNil,
  isArray,
  isObject,
  isString,
  isNumber,
} from 'lodash';
// import nested components
import MapBasic from './MapBasic';
import AppLink from './AppLink';
// import filters
import dateFilters from '@/filters/dateFilters';
import { getTranslationFor, getCountryFlagImg, truncate } from '@/utils/helpers';
import filtersMixin from '@/mixins/filters';

/* The maximum length of a keyword */
const MAX_KEYWORD_LENGTH = 10;

export default {
  name: 'DatasetDetailsDataset',
  dependencies: 'DatasetService',
  components: {
    appLink: AppLink,
    mapBasic: MapBasic,
  },
  mixins: [filtersMixin],
  props: {
    disableIncorrectDateAlert: {
      type: Boolean,
      default: false,
    },
    disableTranslationAlert: {
      type: Boolean,
      default: false,
    },
    disableOptionsDropdown: {
      type: Boolean,
      default: false,
    },
  },
  metaInfo() {
    return {
      meta: [
        {
          name: 'description',
          vmid: 'description',
          content: (this.getDescription
            ? `${this.getTranslationFor(this.getDescription, this.$i18n.locale, this.getLanguages)}`
            : `${this.getTranslationFor(this.getTitle, this.$i18n.locale, this.getLanguages)}`).substr(0, 4999),
        },
        {
          name: 'keywords',
          vmid: 'keywords',
          content: this.getKeywords.map(k => k.title).join(' ').substr(0, 4999),
        },
      ],
      script: [{
        type: 'application/ld+json',
        json: this.datasetSchema,
      }],
    };
  },
  data() {
    return {
      loadingDatasetDetails: false,
      dateIncorrect: false,
      machineTranslated: false,
      translationUpdateProcessing: false,
      translationNotAvailable: false,
      mapVisible: true,
      infoVisible: false,
      visualisationLinkFormats: [
        'csv',
        'xlsx',
        'xls',
      ],
      geoLinkFormats: {
        wms: 'WMS',
        geojson: 'GeoJSON',
        fiware_cb: 'fiware_cb',
        'fiware-cb': 'fiware_cb',
      },
      maxKeywordLength: MAX_KEYWORD_LENGTH,
      datasetSchema: {},
      distributions: {
        displayAll: false,
        displayCount: 7, // Should never exceed number of distributions
        incrementSteps: [10, 50],
      },
    };
  },
  computed: {
    // import store-getters
    ...mapGetters('datasetDetails', [
      'getCatalog',
      'getCategories',
      'getConformsTo',
      'getContactPoints',
      'getCountry',
      'getDescription',
      'getDistributions',
      'getDistributionFormats',
      'getDocumentations',
      'getFrequency',
      'getID',
      'getIdentifiers',
      'getIdName',
      'getKeywords',
      'getLandingPages',
      'getLanguages',
      'getLicences',
      'getLoading',
      'getModificationDate',
      'getOriginalLanguage',
      'getOtherIdentifiers',
      'getProvenances',
      'getPublisher',
      'getRelatedResources',
      'getReleaseDate',
      'getSources',
      'getSpatial',
      'getTranslationMetaData',
      'getTitle',
      'getLegalBase',
      'getGeoCodingDescription',
      'getTemporal'
    ]),
    displayedDistributions() {
      // Freeze objects as distributions won't get modified during runtime
      // Increases performance during rendering
      return this.distributions.displayAll
        ? Object.freeze(this.getDistributions.slice())
        : Object.freeze(this.getDistributions.slice(0, this.distributions.displayCount));
    },
    isDistributionsAllDisplayed() {
      return this.distributions.displayCount === this.getDistributions.length;
    },
    remainingDistributions() {
      return this.getDistributions.length - this.distributions.displayCount;
    }
  },
  created() {
    this.useService(this.DatasetService);
    this.$nextTick(() => {
      this.$Progress.start();
      this.loadingDatasetDetails = true;
      this.loadDatasetDetails(this.$route.params.ds_id)
        .then(() => {
          this.$Progress.finish();
          this.loadingDatasetDetails = false;
          this.datasetSchema = this.getSchemaOrg();
          this.$nextTick(() => {
            // Display/hide translation banners
            this.setTranslationBanners();
            // Activate bootstrap tooltips
            // $('[data-toggle="tooltip"]').tooltip({
            //   container: 'body',
            // });
          });
      });
      this.$root.$on('date-incorrect', () => {
        this.dateIncorrect = true;
      });
    });

    const distributionsConf = this.$env.datasetDetails && this.$env.datasetDetails.distributions;
    if (distributionsConf) {
      this.distributions.displayAll = distributionsConf.displayAll || this.distributions.displayAll;
      this.distributions.displayCount = parseInt(distributionsConf.displayCount, 10) || this.distributions.displayCount;

      if (typeof distributionsConf.incrementSteps === 'string') {
        // Need to parse as json array since env variables are string only
        distributionsConf.incrementSteps = JSON.parse(distributionsConf.incrementSteps);
      }
      this.distributions.incrementSteps = distributionsConf.incrementSteps || this.distributions.incrementSteps;
    }

    if (this.distributions.displayAll) this.distributions.displayCount = this.getDistributions.length;
  },
  destroyed() {
    this.$root.$off('date-incorrect');
  },
  methods: {
    // import store-actions
    ...mapActions('datasetDetails', [
      'loadDatasetDetails',
      'setLoading',
      'useService',
    ]),
    // Lodash has function
    has,
    isNil,
    isArray,
    isObject,
    isString,
    isNumber,
    getTranslationFor,
    getCountryFlagImg,
    truncate,
    getSchemaOfSpatialCoverage() {
      let spatialCoverage = null;

      const spatial = this.getSpatial;
      if (spatial.type === 'Polygon') {
        const places = [];
        const polygons = spatial.coordinates;
        polygons.forEach((polygon) => {
          let poly = '';
          poly = polygon.map(coordinate => coordinate.join(',')).join(' ');
          const geoShape = {
            '@type': 'GeoShape',
            polygon: poly,
          };
          places.push({
            '@type': 'Place',
            geo: geoShape,
          });
        });

        spatialCoverage = places;
      } else if (spatial.type === 'Point') {
        spatialCoverage = {
          '@type': 'Place',
          geo: {
            '@type': 'GeoCoordinates',
            latitude: spatial.coordinates[0],
            longitude: spatial.coordinates[1],
          },
        };
      }

      return spatialCoverage;
    },
    // Returns a json-ld object of the license of the first distribution where a license exists in this dataset
    // or null, if first distribution or license does not exist
    getSchemaOfFirstAvailableLicense() {
      let license = null;
      const licenses = this.getLicences;

      if (licenses && licenses.length > 0) {
        const distributionLicense = this.getLicences.find(l => l.title || l.description || l.id || l.resource);

        if (distributionLicense) {
          const name = distributionLicense.title;
          const description = distributionLicense.description;
          const identifier = distributionLicense.id;
          const url = distributionLicense.resource;

          license = {
            '@type': 'CreativeWork',
            ...name && { name },
            ...description && { description },
            ...identifier && { identifier },
            ...url && { url },
          };
        }
      }
      return license;
    },
    getSchemaOrg() {
      // PAYLOAD
      const payload = {
        '@context': 'http://schema.org',
        '@type': 'Dataset',
          name: '', // https://schema.org/Dataset
        description: '', // https://schema.org/Dataset
        license: {}, // https://schema.org/Dataset
        spatialCoverage: {}, // https://schema.org/Dataset
        keywords: [], // https://schema.org/Dataset
        catalog: {}, // https://schema.org/DataCatalog
        distribution: [], // https://schema.org/DataDownload
        creator: [], // https://schema.org/Organization
        contact: [], // https://schema.org/ContactPoint
      };

      // https://schema.org/Dataset
      payload.name = this.getTranslationFor(this.getTitle, this.$route.query.locale, this.getLanguages).substr(0, 4999);
      payload.description = this.getTranslationFor(this.getDescription, this.$route.query.locale, this.getLanguages).substr(0, 4999);
      payload.license = this.getSchemaOfFirstAvailableLicense();
      payload.spatialCoverage = this.getSchemaOfSpatialCoverage();
      payload.identifier = this.getOtherIdentifiers;
      if (this.showObjectArray(this.getKeywords)) {
        this.getKeywords.forEach((keyword) => {
          if (this.showKeyword(keyword)) {
            payload.keywords.push(keyword.title);
          }
        });
      }

      // https://schema.org/DataCatalog
      payload.catalog = {
        '@type': 'DataCatalog',
        name: this.getTranslationFor(this.getCatalog.title, this.$route.query.locale, this.getLanguages).substr(0, 4999),
        description: this.getTranslationFor(this.getCatalog.description, this.$route.query.locale, this.getLanguages).substr(0, 4999),
        publisher: this.getCatalog.publisher,
        url: this.getCatalog.homepage,
        inLanguage: isArray(this.getCatalog.language) ? this.getCatalog.language[0] : this.getCatalog.language,
        license: this.getCatalog.license,
        dateModified: this.getCatalog.modificationDate,
        spatialCoverage: this.getCatalog.spatial,
      };

      // https://schema.org/DataDownload
      this.getDistributions.forEach((distro) => {
        const distroResult = {
          '@type': 'DataDownload',
          name: this.getDistributionTitle(distro),
          encodingFormat: this.getDistributionFormat(distro),
          licence: distro.licence,
          contentSize: distro.byteSize,
          datePublished: distro.releaseDate,
          dateModified: distro.modificationDate,
        };
        if (this.showAccessUrl(distro)) {
          distroResult.contentUrl = distro.accessUrl;
        }
        payload.distribution.push(distroResult);
      });

      // https://schema.org/Organization
      if (isObject(this.getPublisher) && has(this.getPublisher, 'name') && !isNil(this.getPublisher.name)) {
        payload.creator.push({
          '@type': 'Organization',
          name: this.getPublisher.name,
        });
      }

      // https://schema.org/ContactPoint
      this.getContactPoints.forEach((contactPoint) => {
        if (isObject(contactPoint) && has(contactPoint, 'name') && !isNil(contactPoint.name) && has(contactPoint, 'email') && !isNil(contactPoint.email)) {
          payload.contact.push({
            '@type': 'ContactPoint',
            name: contactPoint.name,
            email: contactPoint.email,
          });
        }
      });

      return payload;
    },
    filterDateFormatUS(date) {
      return dateFilters.formatUS(date);
    },
    filterDateFormatEU(date) {
      return dateFilters.formatEU(date);
    },
    filterDateFromNow(date) {
      return dateFilters.fromNow(date);
    },
    showOriginalLanguage(originalLanguage) {
      return !isNil(originalLanguage);
    },
    getDatasetOriginalLanguage(originalLanguage) {
      return { query: Object.assign({}, this.$route.query, { locale: originalLanguage }) };
    },
    setDatasetOriginalLanguage(originalLanguage) {
      this.$i18n.locale = originalLanguage;
    },
    getDistributionFormat(distribution) {
      return has(distribution, 'format.title') && !isNil(distribution.format.title) ? distribution.format.title : '';
    },
    distributionFormatTruncated(distribution) {
      return this.getDistributionFormat(distribution).length > 4;
    },
    getDistributionTitle(distribution) {
      return distribution.title ? getTranslationFor(distribution.title, this.$i18n.locale, this.getLanguages) : '-';
    },
    getDistributionLegalBase(distribution) {
      return distribution.legalBase ? getTranslationFor(distribution.legalBase, this.$i18n.locale, this.getLanguages) : '-';
    },
    getDistributionDescription(distribution) {
      return (has(distribution, 'description') && !isNil(distribution.description)) ? getTranslationFor(distribution.description, this.$i18n.locale, this.getLanguages) : '-';
    },
    /* ABSTRACT SHOW FUNCTIONS */
    showString(string) {
      return !isNil(string) && isString(string);
    },
    showObject(object) {
      return !isNil(object) && isObject(object) && !Object.values(object).reduce((keyUndefined, currentValue) => keyUndefined && currentValue === undefined, true);
    },
    showArray(array) {
      return !isNil(array) && isArray(array) && array.length > 0;
    },
    showObjectArray(objectArray) {
      return this.showArray(objectArray) && !objectArray.reduce((objectUndefined, currentObject) => objectUndefined && Object.values(currentObject).reduce((keyUndefined, currentValue) => keyUndefined && currentValue === undefined, true), true);
    },
    /* SPECIFIC SHOW FUNCTIONS */
    showLicence(distribution) {
      return (has(distribution, 'licence.title') && !isNil(distribution.licence.title))
          || (has(distribution, 'licence.resource') && !isNil(distribution.licence.resource));
    },
    showLicensingAssistant(distribution) {
      return has(distribution, 'licence.la_url') && !isNil(distribution.licence.la_url);
    },
    showOptionsDropdown(distribution) {
      return this.showVisualisationLink(distribution) || this.showGeoLink(distribution);
    },
    showVisualisationLink(distribution) {
      if (!has(distribution, 'format.title') || isNil(distribution.format.title) || !has(distribution, 'accessUrl') || isNil(distribution.accessUrl)) return false;
      const f = distribution.format.title.toLowerCase();
      // Show VisualisationLink only for formats included in visualisationLinkFormats
      return this.visualisationLinkFormats.includes(f);
    },
    getVisualisationLink(accessUrl) {
      // Return Visualisation Link
      return `/data/visualisation/?file=${accessUrl}`;
    },
    showGeoLink(distribution) {
      if (!has(distribution, 'format.title') || isNil(distribution.format.title) || !has(distribution, 'id') || isNil(distribution.id)) return false;
      const f = distribution.format.title.toLowerCase();
      // Show GeoLink only for formats included in geoLinkFormats
      return Object.keys(this.geoLinkFormats).includes(f);
    },
    getGeoLink(format, distributionID) {
      let f = format.toLowerCase();
      // Use correct Case Sensitive strings
      f = this.geoLinkFormats[f];
      // Return Geo Visualisation Link
      return `/mapapps/?lang=en&type=${f}&dataset=${distributionID}`;
    },
    showDownloadDropdown(distribution) {
      return this.showAccessUrl(distribution) || this.showDownloadUrls(distribution);
    },
    showAccessUrl(distribution) {
      return has(distribution, 'accessUrl') && !isNil(distribution.accessUrl);
    },
    showDownloadUrls(distribution) {
      return has(distribution, 'downloadUrls') && !isNil(distribution.downloadUrls) && isArray(distribution.downloadUrls) && distribution.downloadUrls.length > 0;
    },
    setClipboard(value) {
      const input = document.createElement('INPUT');
      // input.style = "position: absolute; left: -1000px; top: -1000px";
      input.value = value;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    },
    showKeywords(keywords) {
      return !isNil(keywords) && keywords.length > 0;
    },
    showKeyword(keyword) {
      return !isNil(keyword) && has(keyword, 'id') && has(keyword, 'title');
    },
    getKeywordLink(keyword) {
      return { path: `/datasets?keywords=${keyword.id}`, query: Object.assign({}, { locale: this.$route.query.locale }) };
    },
    keywordTruncated(keyword) {
      return keyword.title.length > this.maxKeywordLength;
    },
    showSpatial(spatial) {
      return !isNil(spatial) && has(spatial, 'coordinates') && !isNil(spatial.coordinates) && has(spatial, 'type') && !isNil(spatial.type);
    },
    toggleMap() {
      this.$refs.geocollapse.classList.toggle('show');
      this.mapVisible = !this.mapVisible;
    },
    toggleInfo() {
      this.infoVisible = !this.infoVisible;
    },
    setTranslationBanners() {
      if (!this.$i18n) return;
      const translationMetaData = this.getTranslationMetaData;
      // Check if translation of dataset is available in selected language
      if (!isNil(translationMetaData.details) && has(translationMetaData.details, this.$i18n.locale)) {
        // Check if dataset if machine translated
        this.machineTranslated = translationMetaData.details[this.$i18n.locale].machine_translated;
      } else {
        // No translation of this dataset available for the selected language
        this.translationNotAvailable = true;
      }
      // Check if updated translation of dataset is processing
      if (translationMetaData.status === 'processing') this.translationUpdateProcessing = true;
    },
    // Emit a Matomo event when user clicks on 'go to resource' element
    trackGoto() {
      // eslint-disable-next-line
      const paq = window._paq || [];
      paq.push(['trackEvent', 'GoToResource', 'Clicked']);
    },
    clamp(n, min, max) {
      return Math.min(Math.max(n, min), max);
    },
    // Increases the current number of distributions displayed
    // and clamps the result so that it never exceeds the number of all distributions.
    increaseNumDistributionsDisplayed(increment) {
      const clampedSum = this.clamp(this.distributions.displayCount + increment, 0, this.getDistributions.length);
      this.distributions.displayCount = clampedSum;
    },
    nonOverflowingIncrements(incrementStep) {
      return this.distributions.displayCount + incrementStep <= this.getDistributions.length;
    }
  },
};
</script>

<style lang="scss" scoped>
  @import '~@/styles/bootstrap_theme';

  .heading, .arrow, .copy-text {
    cursor: pointer;
  }
  .text-break {
    word-break: break-word;
  }
  .spacing {
    margin-left: 5px;
    margin-right: 5px;
  }
  .circle {
    width: 40px;
    height: 40px;
    margin: 0 auto;
    padding: 20px 0;
    font-size: 12px;
    line-height: 1px;
    border-radius: 50%;
    background-color: #000000;
    &[type="HTML"] {
      background-color: #00a8cc;
    }
    &[type="JSON"] {
      background-color: #feb72b;
    }
    &[type="XML"] {
      background-color: #feb72b;
    }
    &[type="TXT"] {
      background-color: #b21f66;
    }
    &[type="CSV"] {
      background-color: #ffbd69;
    }
    &[type="XLS"] {
      background-color: #899857;
    }
    &[type="ZIP"] {
      background-color: #21243d;
    }
    &[type="API"] {
      background-color: #ec96be;
    }
    &[type="PDF"] {
      background-color: #ff7c7c;
    }
    &[type="RDF"],
    &[type="NQUAD"],
    &[type="NTRIPLES"],
    &[type="TURTLE"] {
      background-color: #0b4498;
    }
  }

  .resource-dropdown-form {
    width: 500px;
    form {
      max-width: 100%;
      .form-group:last-child {
        margin-bottom: 0;
      }
    }
  }

  /*** BOOTSTRAP ***/
  button:focus {
    outline:0;
  }
  .options, .download {
    .dropdown-menu {
      min-width: 300px;
      .dropdown-item {
        &:hover {
          color: initial;
          background-color: initial;
        }
      }
    }
  }
  /*** MATERIAL ICONS ***/
  .material-icons.small-icon {
    font-size: 20px;
  }

  .distributions {

    &__item {
      position: relative;

      &--preview {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background: linear-gradient(to bottom, rgba(0,0,0,0) 0%, $bg-details-page 55%);
        z-index: 10;
      }
    }

    &__actions {
      display: flex;
      justify-content: center;
      align-items: flex-end;
      height: 100%;
      z-index: 11;
    }
  }
</style>
